import { flatten } from "lodash";
import { useMemo } from "react";
import { useDebounce } from "~/hooks/useDebounce";
import { useDetailedUsers } from "~/hooks/useDetailedUsers";
import { useTeams } from "~/hooks/useTeams";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

export const useMemberSearch = ({
  nodes = {},
  filters = {},
  search = "",
  limit = 50,
}) => {
  const { isAdmin } = useCurrentUser();
  const debouncedInputValue = useDebounce(search, 200);
  const [$givenName = "", $familyName = ""] = debouncedInputValue.split(" ");

  const { getTeamById } = useTeams();

  const filter = useMemo(() => {
    const res = {};
    if (!$familyName) {
      res.or = [
        { givenName: { like: $givenName } },
        { familyName: { like: $familyName || $givenName } },
      ];
    } else {
      res.and = [
        { givenName: { like: $givenName } },
        { familyName: { like: $familyName || $givenName } },
      ];
    }

    return { ...res, ...filters };
  }, [$givenName, $familyName, filters]);

  const {
    status,
    data: response,
    error,
    isFetching,
    isLoading,
    hasNextPage,
    fetchNextPage,
    ...more
  } = useDetailedUsers(
    {
      active: isAdmin,
      first: limit,
      filter,
      node: {
        id: true,
        createdAt: {
          __aliasFor: "createdTS",
        },
        profileImg: {
          __aliasFor: "profileImgImgixURL",
        },
        teamID: true,
        givenName: true,
        familyName: true,
        ...nodes,
      },
    },
    [filter, nodes, limit, isAdmin]
  );

  const data = useMemo(() => {
    return flatten(
      response?.pages.map((data) =>
        (data?.detailedUsers?.edges || [])
          .map((n) => ({
            ...n?.node,
            teamName: getTeamById(n?.node?.teamID)?.name,
            fullName: `${n?.node?.givenName || ""} ${
              n?.node?.familyName || ""
            }`,
            cursor: n.cursor,
          }))
          .filter(({ givenName }) => !!givenName)
      )
    );
  }, [response, getTeamById]);

  return {
    hasNextPage,
    fetchNextPage,
    isFetching,
    isLoading,
    data,
  };
};
